import {createSelector} from 'reselect/es';
import {formValueSelector, getFormValues} from 'redux-form';
import i18next from 'i18next';
import {List} from 'immutable';
import moment from 'moment-timezone';

import PlanBehaviorIds from 'web-app/behaviors/plan-behavior-ids';
import * as PlanConstants from 'web-app/react/entities/plans/constants';
import * as ManagePlanSelectors from 'web-app/react/plans/manage-plan/selectors';
import * as SharedSelectors from 'web-app/react/plans/manage-plan/shared-selectors';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {mergeUnique} from 'web-app/util/array';
import {type RootState} from 'web-app/react/redux/main-reducer';
import {hasValue} from 'web-app/util/typescript';

import * as Helpers from './helpers';
import * as Constants from './constants';

export const setAsHours = (state: RootState) => Boolean(singleValueSelector(state, 'setAsHours'));

export const selectedPublicFunding = (state: RootState) => state.modals.choosePublicFunding.fundingMethod;
export const selectedPublicFundingInstitutionId = (state: RootState) => state.modals.choosePublicFunding.institutionId;
export const selectedPublicFundingDefaultMaxFundedMinutesPerDay = (state: RootState) =>
    state.modals.choosePublicFunding.defaultMaxFundedMinutesPerDay;
export const selectedPublicFundingFrequency = (state: RootState) => singleValueSelector(state, 'frequency');
export const selectedPublicFundingType = (state: RootState) => singleValueSelector(state, 'fundingType');
export const isFetching = (state: RootState) => state.modals.choosePublicFunding.isFetching;

export const institutionIds = createSelector(SharedSelectors.institutionIdFromState, institutionId =>
    institutionId ? [institutionId] : null,
);

export const canChangeFrequency = createSelector(
    SharedSelectors.canChangeDiscountOrFundingFrequency,
    selectedPublicFunding,
    (canChangeFrequency, fundingMethod) => {
        // It should only be possible to change the funding frequency when using
        // manual funding.
        return canChangeFrequency && Helpers.isFundingMethodManual(fundingMethod);
    },
);

const fundingPlanPart = createSelector(SharedSelectors.planParts, planParts => planParts.first());

const billingProfile = createSelector(fundingPlanPart, SharedSelectors.billingProfiles, (planPart, billingProfiles) =>
    billingProfiles.find(billingProfile => billingProfile.billingProfileId === planPart.billingProfileId),
);

const singleValueSelector = formValueSelector(Constants.FORM_NAME);
const formSelector = getFormValues(Constants.FORM_NAME);

// This is used to fetch the values of the form in
// the choose-public-funding-modal. This is the
// combination of the values kept in redux-form and
// the fundingMethod which is contained in the
// modals.choosePublicFundingModal state slice.
// It is used for validation.
export const publicFundingValues = createSelector(formSelector, selectedPublicFunding, (formValues, fundingMethod) => {
    return {...formValues, fundingMethod};
});

const formValues = createSelector(
    state => formSelector(state),
    formValues => formValues,
);

const publicFundingPlanBehaviors = createSelector(
    state => state.modals.choosePublicFunding.planPublicFundingBehaviors,
    SharedSelectors.planBehaviors,
    (behaviors, planBehaviors) => {
        if (behaviors) {
            return behaviors;
        }
        return planBehaviors || new BehaviorsCollection([]);
    },
);
export const publicFundingPreviewState = createSelector(
    SharedSelectors.activePublicFundingPreviewPlanState,
    activeState => {
        if (!activeState) {
            return null;
        }

        return activeState.publicFunding;
    },
);

export const canEditRate = createSelector(
    publicFundingPlanBehaviors,
    selectedPublicFunding,
    (fundingBehaviors, selectedFundingMethod) => {
        const fundingMethodsThatCanEditRate = fundingBehaviors.behaviorForId(PlanBehaviorIds.CanSetPFRateOnPlan)
            ?.payload.methods;

        return fundingMethodsThatCanEditRate.includes(selectedFundingMethod);
    },
);

export const canEditTotal = createSelector(
    publicFundingPlanBehaviors,
    selectedPublicFunding,
    (fundingBehaviors, selectedFundingMethod) => {
        const fundingMethodsThatCanEditRate = fundingBehaviors.behaviorForId(PlanBehaviorIds.CanSetPFTotalOnPlan)
            .payload.methods;

        return fundingMethodsThatCanEditRate.includes(selectedFundingMethod);
    },
);

const dayToGetActiveRates = createSelector(SharedSelectors.activePublicFundingPreviewPlanState, activeState => {
    const today = moment().startOf('day');
    if (!activeState) {
        return today;
    } else if (activeState.to && activeState.to.isSameOrBefore(today, 'day')) {
        return activeState.to;
    } else if (activeState.from && activeState.from.isSameOrAfter(today, 'day')) {
        return activeState.from;
    }

    return today;
});

const getInitialFundingTypeId = createSelector(SharedSelectors.planPublicFundingSettings, publicFundingSettings => {
    const planFundingTypeIds = (publicFundingSettings && publicFundingSettings.fundingTypeIds) || List();
    return planFundingTypeIds.first();
});

export const fundingQueryVariables = createSelector(
    institutionIds,
    getInitialFundingTypeId,
    (institutionIds, initialFundingTypeId) =>
        institutionIds
            ? {
                  institutionIds,
                  fundingTypeIds: initialFundingTypeId ? [initialFundingTypeId] : [],
              }
            : null,
);

export const hasPlanPublicFunding = createSelector(SharedSelectors.planPublicFundingSettings, publicFundingSettings =>
    Boolean(publicFundingSettings),
);

export const hasLoadedPreview = createSelector(
    state => state.modals.choosePublicFunding.hasFetchedPreview,
    hasPlanPublicFunding,
    (hasFetchedPreview, hasPlanPublicFunding) => hasFetchedPreview || hasPlanPublicFunding,
);

export const publicFundingNotices = createSelector(publicFundingPlanBehaviors, behaviors => {
    if (behaviors.includes(PlanBehaviorIds.ShowPlanPublicFundingNotices)) {
        return behaviors.behaviorForId(PlanBehaviorIds.ShowPlanPublicFundingNotices).payload.notices;
    }

    return [];
});

const warningsToShow = [1508925108];
const MANUAL_FUNDING_WARNING = 1569237686;
const FUNDING_TYPE_REQUIRED_WARNING = 1537270009;

const allPublicFundingWarnings = createSelector(publicFundingPlanBehaviors, behaviors => {
    if (behaviors.includes(PlanBehaviorIds.ShowPlanWarnings)) {
        return behaviors.behaviorForId(PlanBehaviorIds.ShowPlanWarnings).payload.warnings;
    }

    return [];
});

export const publicFundingWarnings = createSelector(allPublicFundingWarnings, allPublicFundingWarnings =>
    allPublicFundingWarnings.filter(warning => warningsToShow.includes(warning.error)),
);

export const fundingTypeIsRequired = createSelector(allPublicFundingWarnings, allWarnings =>
    allWarnings.some(warning => warning.error === FUNDING_TYPE_REQUIRED_WARNING),
);

export const manualFundingWarning = createSelector(allPublicFundingWarnings, allWarnings =>
    allWarnings.find(warning => warning.error === MANUAL_FUNDING_WARNING),
);

export const canSavePublicFunding = createSelector(publicFundingPlanBehaviors, publicFundingBehaviors =>
    publicFundingBehaviors.includes(PlanBehaviorIds.CanSavePublicFunding),
);

export const canSelectAssistedFunding = createSelector(SharedSelectors.publicFundingMethods, types =>
    types.includes(PlanConstants.PUBLIC_FUNDING_TYPE.ASSISTED),
);

export const publicFundingSetup = createSelector(canSelectAssistedFunding, canSelectAssistedFunding => {
    return {
        assistedPublicFundingIsAvailable: canSelectAssistedFunding,
    };
});

const getAllOrBillingProfileFundingTypes = createSelector(
    (_, props) => props.fundingTypes,
    billingProfile,
    (fundingTypes, billingProfile) =>
        billingProfile && billingProfile.fundingTypes ? billingProfile.fundingTypes : fundingTypes,
);

const mergeFundingTypesWithExisting = createSelector(
    getAllOrBillingProfileFundingTypes,
    (_, props) => props.existingTypes,
    (fundingTypes, existingTypes) => {
        if (fundingTypes && existingTypes) {
            return mergeUnique(existingTypes, fundingTypes, (fundingType: any) => fundingType.id);
        } else if (fundingTypes || existingTypes) {
            return fundingTypes || existingTypes;
        }

        return [];
    },
);

const getAvailableFundingTypes = createSelector(mergeFundingTypesWithExisting, fundingTypes =>
    fundingTypes.filter(f => f.__typename === 'FundingPlan'),
);

const fundingTypesWithAgeRange = createSelector(getAvailableFundingTypes, fundingTypes =>
    fundingTypes.filter(f => f.ageRange),
);

const defaultFundingType = createSelector(
    fundingTypesWithAgeRange,
    ManagePlanSelectors.childMonthsOldAtPlanStart,
    (fundingTypesWithAgeRange, childMonthsOldAtPlanStart) =>
        fundingTypesWithAgeRange.find(
            f => f.ageRange.from <= childMonthsOldAtPlanStart && f.ageRange.to >= childMonthsOldAtPlanStart,
        ),
);

const defaultFundingTypeId = createSelector(defaultFundingType, defaultFundingType =>
    defaultFundingType ? defaultFundingType.id : '',
);

const getInitialFundingTypeData = createSelector(
    getInitialFundingTypeId,
    hasPlanPublicFunding,
    defaultFundingTypeId,
    (initialFundingTypeId, hasPublicFunding, defaultFundingTypeId) => {
        return {
            fundingType: hasPublicFunding ? initialFundingTypeId : defaultFundingTypeId,
        };
    },
);

const defaultInitialValues = createSelector(
    getInitialFundingTypeData,
    selectedPublicFundingDefaultMaxFundedMinutesPerDay,
    (initialFundingTypeData, defaultMaxFundedMinutesPerDay) => ({
        frequency: PlanConstants.FREQUENCY.WEEKLY,
        setAsHours: false,
        maxFundedMinutes: defaultMaxFundedMinutesPerDay,
        ...initialFundingTypeData,
    }),
);

export const initialValues = createSelector(
    SharedSelectors.planPublicFundingSettings,
    defaultInitialValues,
    (publicFundingSettings, defaultInitialValues) => {
        if (!publicFundingSettings) {
            return defaultInitialValues;
        }
        const {method} = publicFundingSettings;

        const initialValues = {
            ...defaultInitialValues,
            maxFundedMinutes: hasValue(publicFundingSettings.maxFundedMinutes)
                ? publicFundingSettings.maxFundedMinutes
                : defaultInitialValues.maxFundedMinutes,
        };

        if (method === PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL) {
            if (publicFundingSettings.rate) {
                initialValues.rate = Helpers.formatNumberForForm(publicFundingSettings.rate);
            } else if (publicFundingSettings.minutes !== null && publicFundingSettings.minutes) {
                initialValues.rate = Helpers.formatNumberForForm(
                    publicFundingSettings.amount / (publicFundingSettings.minutes / 60),
                );
            } else if (publicFundingSettings.hours) {
                initialValues.rate = Helpers.formatNumberForForm(
                    publicFundingSettings.amount / publicFundingSettings.hours,
                );
            }

            initialValues.frequency = publicFundingSettings.period;
            initialValues.hours = Helpers.formatNumberForForm(publicFundingSettings.hours || 0);
            initialValues.minutes = publicFundingSettings.minutes || 0;
            initialValues.setAsHours = publicFundingSettings.minutes === null;
            initialValues.amount = Helpers.formatNumberForForm(publicFundingSettings.amount);
        } else if (method === PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL_BY_RATE) {
            initialValues.rate = Helpers.formatNumberForForm(publicFundingSettings.rate);
            initialValues.frequency = publicFundingSettings.period;
            initialValues.hours = Helpers.formatNumberForForm(publicFundingSettings.hours || 0);
            initialValues.minutes = publicFundingSettings.minutes || 0;
            initialValues.setAsHours = publicFundingSettings.minutes === null;
            initialValues.amount = Helpers.formatNumberForForm(publicFundingSettings.amount);
        } else if (method === PlanConstants.PUBLIC_FUNDING_TYPE.ASSISTED) {
            initialValues.setAsHours = publicFundingSettings.minutes === null;
            initialValues.minutes = publicFundingSettings.minutes || 0;
            initialValues.hours = Helpers.formatNumberForForm(publicFundingSettings.hours || 0);
        }

        return initialValues;
    },
);

const fundedBookings = createSelector(publicFundingPreviewState, publicFundingPreview => {
    if (publicFundingPreview) {
        return publicFundingPreview.fundedBookings;
    }

    return List();
});

const selectedFundingTypes = createSelector(
    getAvailableFundingTypes,
    selectedPublicFundingType,
    (availableFundingTypes, selectedFundingType) => {
        return availableFundingTypes.filter(type => type.id === selectedFundingType);
    },
);

const getClaimableRates = createSelector(
    selectedFundingTypes,
    dayToGetActiveRates,
    (selectedFundingTypes, activeDay) => {
        return selectedFundingTypes
            .map(fundingType => {
                const activeRatePeriod = Helpers.getActiveFundingTypePeriod(fundingType, activeDay);

                return activeRatePeriod
                    ? activeRatePeriod.rates.reduce((sum, current) => sum + Number(current.rate), 0)
                    : null;
            })
            .filter(rate => rate !== null);
    },
);

export const fundingTypeOptions = createSelector(
    getAvailableFundingTypes,
    dayToGetActiveRates,
    (availableFundingTypes, activeDay) =>
        availableFundingTypes.map(fundingType => {
            return {
                subtitle: Helpers.getFundingTypeSubtitle(fundingType, activeDay),
                label: fundingType.title,
                value: fundingType.id,
            };
        }),
);

// To be sure that there is claimable funding types there has to be funded bookings in the public funding preview
export const hasClaimableFunding = createSelector(
    hasLoadedPreview,
    fundedBookings,
    getClaimableRates,
    (hasLoadedPreview, fundedBookings, claimableRates) => {
        return !hasLoadedPreview || fundedBookings.size > 0 || claimableRates.length > 0;
    },
);

export const publicFundingPreview = createSelector(
    publicFundingPreviewState,
    getClaimableRates,
    formValues,
    (publicFundingPreview, claimableRates, formValues) => {
        const hourlyRate = claimableRates.reduce((reduced, current) => reduced + current, 0);
        const publicFundingObj = publicFundingPreview ? publicFundingPreview.toJS() : {};
        const isHours = publicFundingObj.hours !== null;
        const duration = isHours ? parseFloat(publicFundingObj.hours) : parseFloat(publicFundingObj.minutes) * 60;
        const hours = duration || 0;
        const value = parseFloat(publicFundingObj.amount) || 0;
        return {
            ...publicFundingObj,
            value,
            hours,
            hourlyRate,
            rate: formValues && formValues.rate,
            claimable: publicFundingObj.claimable || 0,
        };
    },
);

const previewPublicFundingSettings = createSelector(
    formValues,
    selectedPublicFunding,
    (formValues, selectedFundingMethod) => {
        return Helpers.formValuesToPublicFundingSettings(formValues, selectedFundingMethod);
    },
);

export const publicFundingPlan = createSelector(
    ManagePlanSelectors.previewPlan,
    previewPublicFundingSettings,
    formValues,
    (plan, publicFundingSettings, formValues) => {
        const defaultPlan = {
            ...plan,
            publicFundingSettings: undefined,
        };

        if (!formValues) {
            return defaultPlan;
        }
        return {
            ...defaultPlan,
            publicFundingSettings,
        };
    },
);

const firstTermTimeOnly = createSelector(SharedSelectors.firstPlanPart, firstPlanPart => {
    return firstPlanPart.termTimeOnly;
});

export const attendance = createSelector(firstTermTimeOnly, termTimeOnly => {
    return termTimeOnly
        ? i18next.t('plans.managePlan.pricing.termTimeOnly')
        : i18next.t('plans.managePlan.pricing.stretched');
});
